/**
 * Where this visitor came from.
 *
 * A link like `?src=youtube`, or a doctor's QR (`?src=dr_qr&ref=DOC001`), is remembered
 * for 30 days and sent with every order, appointment and prescription request. The first
 * touch is not overwritten by later page loads unless a new `?src=` arrives, so a patient
 * who scans a doctor's poster today and buys next week is still credited to that doctor.
 */
const KEY = "iv_attribution";
const DAYS = 30;

const clean = (value, allowed) =>
  String(value || "")
    .trim()
    .replace(allowed, "")
    .slice(0, 40);

export function readStored() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!saved?.at) return null;
    if (Date.now() - saved.at > DAYS * 86400000) {
      localStorage.removeItem(KEY);
      return null;
    }
    return saved;
  } catch {
    return null;
  }
}

/** Reads `?src=` / `?ref=` off the current URL and stores them. Safe to call on every load. */
export function captureAttribution(search = window.location.search) {
  const params = new URLSearchParams(search || "");
  const src = clean(params.get("src"), /[^A-Za-z0-9_-]/g).toLowerCase();
  const ref = clean(params.get("ref"), /[^A-Za-z0-9_-]/g).toUpperCase();
  if (!src && !ref) return readStored();
  const stored = readStored();
  const next = {
    src: src || stored?.src || "direct",
    ref: ref || (src ? "" : stored?.ref || ""),
    at: Date.now(),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private browsing — attribution is best effort, never a blocker */
  }
  return next;
}

/** The two fields to send with anything the visitor creates. */
export function attribution() {
  const stored = readStored();
  return { src: stored?.src || "direct", ref: stored?.ref || "" };
}
